
import React, { useMemo, useState } from 'react';
import { withStyles, Menu, MenuItem, ListItemIcon, ListItemText, Radio, Accordion, AccordionSummary, AccordionDetails, Typography,  Divider, Icon} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import { findTriggerEvent } from '../../../utils/commonUtils';
import { getDataFetchTransactions, getFunctionalTransactions } from '../../../utils/transactionUtils';
import { SimulationIcon, SystemModelIcons, TransactionIcon, ViewIcon } from '../icons';

const StyledMenu = withStyles({
  paper: {
    borderRadius: 16,
    backgroundColor: '#fff',
    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
    minWidth: 300,
    maxWidth: 500,
    padding: 6,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
})((props) => (
  <Menu
    elevation={0}
    anchorReference="anchorPosition"

    /* 🔑 menu top-left is anchored to click */
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}

    /* 🔑 height grows downward */
    transformOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}

    /* 🔒 prevent repositioning */
    getContentAnchorEl={null}
    disableAutoFocusItem
    disableEnforceFocus
    disableScrollLock
    marginThreshold={0}

    {...props}
  />
));



const StyledMenuItem = withStyles({
  root: {
    padding: '1px 8px',
    borderRadius: 8,
    marginBottom: 2,
  },
})(MenuItem);

const StyledAccordion = withStyles({
  root: {
    margin: 0,
    border: 'none', 
    boxShadow: 'none',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    '&$expanded': {
      margin: '6px 0', 
      borderBottom: 'none',
    },
    '&:before': {
      display: 'none',  
    },
    '&:not($expanded)': {
      borderBottom: 'none',
    },
    '&:last-child': {
      borderBottom: 'none',
    },

  },
  expanded: {},
})(Accordion);


const StyledAccordionSummary = withStyles({
  root: {
    minHeight: 36,             
    padding: '0 10px', 
    '&$expanded': {
      minHeight: 32,
    },
  },
  content: {
    margin: '6px 0', 
    '&$expanded': {
      margin: '6px 0',
    },
  },
  expanded: {},
})(AccordionSummary);

const StyledAccordionDetails = withStyles({
  root: {
    padding: '4px 6px 6px',    
    maxHeight: 220,
    overflowY: 'auto', 
  },
})(AccordionDetails);


// MenuItem content: radio button + two rows text
const MenuItemContent = ({ title, description, selected, onClick, isCreate = false }) => (
  <StyledMenuItem onClick={onClick}>
    <ListItemIcon style={{ minWidth: 32 }}>   {/* ✅ tightened spacing */}
      <Radio size="small" checked={selected} />
    </ListItemIcon>
    <ListItemText
      primary={title}
      secondary={description}
      primaryTypographyProps={{
        style: { 
          fontSize: 12, 
          fontWeight: 400,   // ✅ NOT BOLD anymore
          lineHeight: 1.2 
        }
      }}
      secondaryTypographyProps={{ 
        style: { 
          fontSize: 10, 
          color: '#777', 
          lineHeight: 1.2,

          display: '-webkit-box',
          WebkitLineClamp: 2,        // 👈 Number of visible lines (change to 1 if needed)
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      }}
    />
  </StyledMenuItem>
);

const SectionHeader = ({ title, icon }) => (
  <StyledAccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon &&
        React.cloneElement(icon, {
          style: {
            fontSize: 16,   // 👈 smaller icon
            width: 18,
            height: 18,
            color: '#555',
        },
        })}
      <Typography style={{ fontSize: 12, fontWeight: 700 }}>
        {title}
      </Typography>
    </div>
  </StyledAccordionSummary>
);




export default function CustomPromptMenu({ anchorEl, menuPosition, setAnchorEl, items, setActiveMenu, activeMenu, clusterlets, transactions, events}) {
  const GROUP_CONFIG = {
    system: {
      icon: <SystemModelIcons />,
      title: "System Model",
      dependsOn: null,
    },
    view: {
      icon: <ViewIcon />,
      title: "Views",
      dependsOn: null,
    },
    transaction: {
      icon: <TransactionIcon />,
      title: "Transactions",
      dependsOn: "view",
    },
    testsuites: {
      icon: <SimulationIcon />,
      title: "Testsuites",
      dependsOn: "transaction",
    },
  };


  const [expandedId, setExpandedId] = useState(null);

  //Accordion control (Only one open at a time)
  const handleAccordionChange = (id) => (event, isExpanded) => {
    setExpandedId(isExpanded ? id : null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSelect = (item) => {
    setActiveMenu(item);
    handleClose();
  };

  const groupedItems = useMemo(() => {
    const map = {};
    items.forEach(item => {
      if (!map[item.group]) map[item.group] = [];
      map[item.group].push(item);
    });
    return map;
  }, [items]);



  

  const isGroupEnabled = (groupKey) => {
    const clusterCount = clusterlets?.length || 0;
  
    if(groupKey === "system"){
      return true;
    }
    // ✅ SYSTEM & VIEWS:
    if ( groupKey === "view") {
      return clusterCount > 1;
    }
  
    // ✅ TRANSACTION:
    if (groupKey === "transaction") {
      if (clusterCount <= 1) return false;
      const functionalTransactions = getFunctionalTransactions({transactions, events, clusterlets})
      const llmGeneratedTransactions = transactions.find(tr => tr.type == 7 && tr.helperText != undefined );
      const llmGeneratedFetchTransactions = getDataFetchTransactions({transactions:llmGeneratedTransactions, events, clusterlets})
  
      const viewItems = groupedItems["view"] || [];
      return viewItems.some(i => i.type === "existing") || functionalTransactions?.length > 0 || llmGeneratedFetchTransactions.length > 0;
    }
  
    // ✅ TESTSUITES:
    if (groupKey === "testsuites") {
      const functionalTransactions = getFunctionalTransactions({transactions, events, clusterlets}) || []
      if(functionalTransactions?.length > 0){
        return true
      }else{
        return false
      }
    }
  
    return false;
  };

  //SECTION RENDERER (REUSABLE)
  const renderSection = (id, title, icon, data,  disabled) => {
    const createItem = data.find(i => i.type === 'create');
    const existingItems = data.filter(i => i.type === 'existing');

    return (
      <StyledAccordion
        key={id}
        expanded={expandedId === id}
        onChange={disabled ? undefined : handleAccordionChange(id)}
        disabled={disabled}
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          borderBottom: '1px solid rgb(237 237 237)'
        }}
      >
        <SectionHeader icon={icon} title={title} />

        <StyledAccordionDetails style={{ display: 'block', padding: 3 }}>

          {/*CREATE */}
          {createItem && (
            <MenuItemContent
              title={createItem.title}
              description={createItem.description}
              selected={activeMenu?.id === createItem.id}
              isCreate
              onClick={() => onSelect(createItem)}
            />
          )}

          {/*EXISTING */}
          {existingItems.map((item) => (
            <MenuItemContent
              key={item.id}
              title={item.title}
              description={item.description}
              selected={activeMenu?.id === item.id}
              onClick={() => onSelect(item)}
            />
          ))}

        </StyledAccordionDetails>
      </StyledAccordion>
    );
  };

  return (
    <StyledMenu 
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorPosition={menuPosition}
    >
      {Object.keys(GROUP_CONFIG).map(groupKey => {
        const data = groupedItems[groupKey] || [];
        const enabled = isGroupEnabled(groupKey);
        return renderSection(groupKey, GROUP_CONFIG[groupKey].title, GROUP_CONFIG[groupKey].icon,  data, !enabled  );
      })}
    </StyledMenu>
  );
}
